import { useState, useMemo, useCallback } from 'react';
import { useUrl } from 'crossroad';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { containsPassage, findFirstLeafSectionId, collectUniqueFormsFromOutline } from '@/lib/outline/outline-tree-utils';
import { getFormBorderColor } from '@/lib/utils/form-colors';

interface OutlineNodeItemProps {
    node: OutlineNode;
    depth?: number;
    passageId?: string;
    showAll?: boolean;
    visibleNodeIds?: Set<string>;
    passageMap: Map<string, Passage>;
}

function OutlineNodeItem({ node, depth = 0, passageId, showAll = false, visibleNodeIds, passageMap }: OutlineNodeItemProps) {
    const [, setUrl] = useUrl();

    // Check if this node directly contains the current passage (not descendants)
    const isDirectMatch = passageId ? node.sectionIds?.includes(passageId) : false;
    // Check if any descendant contains the passage
    const hasDescendantMatch = passageId ? (!isDirectMatch && containsPassage(node, passageId)) : false;

    // Clean the title by removing common prefixes like "I.", "A.", "1.", etc.
    const cleanTitle = node.title.replace(/^[IVXivx]+\.|^[A-Z]\.|^[0-9]+\.\s*/, '').trim();

    // Get the first section ID for this node (for linking and color)
    const firstSectionId = findFirstLeafSectionId(node);

    // Get the passage for this node to determine form/genre color
    const firstPassage = firstSectionId ? passageMap.get(firstSectionId) : undefined;
    const borderColor = getFormBorderColor(firstPassage?.form, firstPassage?.genre);

    const handleRefClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        if (firstSectionId) {
            setUrl(`/app/passage/${firstSectionId}/context/book`);
        }
    }, [firstSectionId, setUrl]);

    // Create genre info label for tooltip
    const genreLabel = firstPassage?.form
        ? `${firstPassage.form}${firstPassage.genre ? ` (${firstPassage.genre})` : ''}`
        : firstPassage?.genre || 'Unknown';

    return (
        <>
            <div className="flex items-start gap-4 py-0.5" style={{ paddingLeft: `${depth * 1.5}rem` }}>
                {/* Colored genre indicator with tooltip */}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div
                            className="w-1 h-5 rounded-sm flex-shrink-0 mt-0.5"
                            style={{ backgroundColor: borderColor }}
                        />
                    </TooltipTrigger>
                    <TooltipContent side="left">
                        <p className="text-xs">{genreLabel}</p>
                    </TooltipContent>
                </Tooltip>

                {/* Node content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                        {/* Reference (clickable) */}
                        <button
                            onClick={handleRefClick}
                            className="text-xs text-primary hover:underline cursor-pointer font-mono flex-shrink-0"
                        >
                            {node.ref}
                        </button>

                        <span className={`text-xs ${
                            isDirectMatch ? 'font-bold text-foreground' :
                            hasDescendantMatch ? 'text-foreground' :
                            'text-foreground'
                        }`}>{cleanTitle}</span>
                    </div>
                </div>
            </div>

            {/* Render children */}
            {node.children?.length > 0 && (
                <>
                    {node.children
                        .filter(child => showAll || !visibleNodeIds || visibleNodeIds?.has(child.id))
                        .map((child) => (
                            <OutlineNodeItem
                                key={child.id}
                                node={child}
                                depth={depth + 1}
                                passageId={passageId}
                                showAll={showAll}
                                visibleNodeIds={visibleNodeIds}
                                passageMap={passageMap}
                            />
                        ))}
                </>
            )}
        </>
    );
}

interface BookOutlineSectionProps {
    outline: Outline;
    passageId?: string;
    passageMap: Map<string, Passage>;
    visibleNodeIds?: Set<string>;
    onGenreClick?: (form: string | undefined, genre: string | undefined) => void;
}

export function BookOutlineSection({ outline, passageId, passageMap, visibleNodeIds, onGenreClick }: BookOutlineSectionProps) {
    const [showFullOutline, setShowFullOutline] = useState(false);
    const [showLegend, setShowLegend] = useState(false);

    // Collect unique forms from visible outline nodes for the legend
    const legendItems = useMemo(() => {
        return collectUniqueFormsFromOutline(outline.structure, passageMap, showFullOutline ? undefined : visibleNodeIds);
    }, [outline.structure, passageMap, showFullOutline, visibleNodeIds]);

    return (
        <div>
            <div className="space-y-0.5">
                {outline.structure
                    .filter(node => showFullOutline || !visibleNodeIds || visibleNodeIds.has(node.id))
                    .map((node) => (
                        <OutlineNodeItem
                            key={node.id}
                            node={node}
                            depth={0}
                            passageId={passageId}
                            showAll={showFullOutline}
                            visibleNodeIds={visibleNodeIds}
                            passageMap={passageMap}
                        />
                    ))}
            </div>
            <div className="mt-4 flex justify-center">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFullOutline(!showFullOutline)}
                >
                    {showFullOutline ? 'Show Current Context Only' : 'Show Full Outline'}
                </Button>
            </div>

            {/* Legend Toggle and Content */}
            {legendItems.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                    <button
                        onClick={() => setShowLegend(!showLegend)}
                        className="text-xs text-primary hover:underline cursor-pointer mb-3 flex items-center gap-1"
                    >
                        {showLegend ? (
                            <>
                                <ChevronUp className="h-3 w-3" />
                                Hide Genre Color Legend
                            </>
                        ) : (
                            <>
                                <ChevronDown className="h-3 w-3" />
                                Show Genre Color Legend
                            </>
                        )}
                    </button>

                    {showLegend && (
                        <div className="space-y-1 mt-3">
                            {legendItems.map((item, index) => {
                                // Build the hierarchy display
                                const parts: string[] = [];
                                if (item.genre) parts.push(item.genre);
                                if (item.subgenre && item.subgenre !== item.genre) parts.push(item.subgenre);
                                if (item.form && item.form !== item.subgenre) parts.push(item.form);

                                return (
                                    <button
                                        key={`${item.genre}-${item.subgenre}-${item.form}-${index}`}
                                        className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 px-2 py-1.5 rounded transition-colors w-full text-left"
                                        onClick={() => onGenreClick?.(item.form, item.genre)}
                                        title="Click to learn more about this literary form"
                                    >
                                        <div
                                            className="w-3 h-3 rounded-sm flex-shrink-0"
                                            style={{ backgroundColor: getFormBorderColor(item.form, item.genre) }}
                                        ></div>
                                        <span className="text-xs text-foreground">
                                            {parts.map((part, i) => (
                                                <span key={i}>
                                                    {i > 0 && <span className="text-muted-foreground mx-1">→</span>}
                                                    <span className={i === parts.length - 1 ? 'font-medium' : 'text-muted-foreground'}>
                                                        {part}
                                                    </span>
                                                </span>
                                            ))}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

